import {PageError403} from '@/pages/Errors/403'
import {PageError500} from '@/pages/Errors/500'
import {createBrowserRouter, RouterProvider} from 'react-router-dom'
import {ROUTES} from '@/shared/constants/routesConstants'
import {ProtectedRoutes} from './ProtectedRoutes'
import {setShellActive, setShellPath} from '@/store/slices/commonSlice'
import {PageError404} from '@/pages'
import {Logout} from '@/pages/Logout'
import {useAppDispatch} from '@/store'
import {useEffect, lazy, Suspense} from 'react'
import {ImageOptimization} from '@/containers/ImageOptimization'
import { setCountryCode } from '@/store/slices/countryCodeSlice'
import { useGrowthBookFeatureFlags } from '@/hooks/use-growthbook-feature-flags-hook'
import { GrowthBookProvider } from '@growthbook/growthbook-react'
import TableSkeletonLoader from '@/components/atoms/SkeletonLoader/TableSkeletonLoader/TableSkeletonLoader'

// Lazy load route-level components for code splitting
const OrderDetail = lazy(() => import('@/features/Order/pages/OnlineOrderDetailsPage/OnlineOrderDetail').then(m => ({default: m.OnlineOrderDetail})))
const Orders = lazy(() => import('@/features/Order/pages/Orders').then(m => ({default: m.Orders})))
const OrdersItemDetail = lazy(() => import('@/features/Order/pages/OrderItemDetailsPage/OrdersItemDetail').then(m => ({default: m.OrdersItemDetail})))
const OfflineOrderDetail = lazy(() => import('@/features/Order/pages/OfflineOrderDetailsPage/OfflineOrderDetail').then(m => ({default: m.OfflineOrderDetail})))
const RefundedOrderDetail = lazy(() => import('@/features/Order/pages/RefundedOrderDetailsPage/RefundedOrderDetail').then(m => ({default: m.RefundedOrderDetail})))
const RewardsOrderDetail = lazy(() => import('@/features/Order/pages/RewardsOrderDetailsPage/RewardsOrderDetail').then(m => ({default: m.RewardsOrderDetail})))

// Delivery section (lazy)
const DeliveryOverview = lazy(() => import('@/features/Delivery/pages/DeliveryOverview').then(m => ({default: m.DeliveryOverview})))
const DeliveryRoutes = lazy(() => import('@/features/Delivery/pages/DeliveryRoutes').then(m => ({default: m.DeliveryRoutes})))
const DeliveryHolidays = lazy(() => import('@/features/Delivery/pages/DeliveryHolidays').then(m => ({default: m.DeliveryHolidays})))
const DeliveryEvents = lazy(() => import('@/features/Delivery/pages/DeliveryEvents').then(m => ({default: m.DeliveryEvents})))
const DeliveryMOA = lazy(() => import('@/features/Delivery/pages/DeliveryMOA').then(m => ({default: m.DeliveryMOA})))
const DeliveryMOV = lazy(() => import('@/features/Delivery/pages/DeliveryMOV').then(m => ({default: m.DeliveryMOV})))


// Wrap lazy components in Suspense
const withSuspense = (Component) => (
  <Suspense fallback={<TableSkeletonLoader rows={8} rowHeight={48} spacing={8} />}>
    <Component />
  </Suspense>
)

const orderRoutes = [
  {path: ROUTES.ONLINE_ORDER_DETAIL_PAGE, element: withSuspense(OrderDetail)},
  {path: ROUTES.ORDER_LIST, element: withSuspense(Orders)},
  {path: ROUTES.ORDERS_ITEM_DETAIL, element: withSuspense(OrdersItemDetail)},
  {path: ROUTES.OFFLINE_ORDER_DETAIL_PAGE, element: withSuspense(OfflineOrderDetail)},
  {path: ROUTES.REFUNDED_ORDER_DETAIL_PAGE, element: withSuspense(RefundedOrderDetail)},
  {path: ROUTES.REWARDS_ORDER_DETAIL_PAGE, element: withSuspense(RewardsOrderDetail)},
]

const deliveryRoutes = [
  {path: ROUTES.DELIVERY_OVERVIEW, element: withSuspense(DeliveryOverview)},
  {path: ROUTES.DELIVERY_ROUTES, element: withSuspense(DeliveryRoutes)},
  {path: ROUTES.DELIVERY_HOLIDAYS, element: withSuspense(DeliveryHolidays)},
  {path: ROUTES.DELIVERY_EVENTS, element: withSuspense(DeliveryEvents)},
  {path: ROUTES.DELIVERY_MOA, element: withSuspense(DeliveryMOA)},
  {path: ROUTES.DELIVERY_MOV, element: withSuspense(DeliveryMOV)},
]
const router = createBrowserRouter([
  {
    element: <ProtectedRoutes />,
    children: [
      {path: ROUTES.HOME, element: withSuspense(Orders)},
      {path: ROUTES.LOGOUT, element: <Logout />},
      {path: ROUTES.IMAGE, element: <ImageOptimization />},

      /* Errors */
      {path: ROUTES.ERROR_500, element: <PageError500 />},
      {path: ROUTES.ERROR_403, element: <PageError403 />},
      {path: '*', element: <PageError404 />},

      /* Orders */
      ...orderRoutes,

      /* Delivery */
      ...deliveryRoutes,
    ],
  },
])

export const AppRouter = ({shellActive, path, market}) => {
  const dispatch = useAppDispatch()
  const {growthbook} = useGrowthBookFeatureFlags()

  useEffect(() => {
    dispatch(setShellActive(shellActive))
    if (market) {
      dispatch(setCountryCode(market))
    }
  }, [shellActive, dispatch, market])

  // expected everytime to dispatch the shell path when component is mounted to navigate properly
  useEffect(() => {
    dispatch(setShellPath(path))
  }, [dispatch, path])

  return (
    <GrowthBookProvider growthbook={growthbook}>
      <RouterProvider router={router} />
    </GrowthBookProvider>
  )
}
